import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useApiGateway } from 'hooks/useApi';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { CompanyProfile, CompanyQuote, emptyProfile, emptyQuote } from 'constants/stockApi';


interface TickerDetails {
  profile: CompanyProfile;
  quote: CompanyQuote;
}

interface DisplayedDetails {
  price: number;
  marketCap: number;
  dayHigh: number;
  dayLow: number;
  yearHigh: number;
  yearLow: number;
  priceAvg50: number;
  priceAvg200: number;
}

const defaultTickerDetails = {
  profile: emptyProfile,
  quote: emptyQuote,
};

interface SidebarTickerDetailsProps {
  ticker: string;
}

const SidebarTickerDetails: React.FC<SidebarTickerDetailsProps> = ({ ticker }) => {
  const [tickerDetails, setTickerDetails] = useState<TickerDetails>(defaultTickerDetails);
  const { get, isLoading } = useApiGateway<never, TickerDetails>();

  const details: DisplayedDetails = {
    price: tickerDetails.quote.price || tickerDetails.profile.price || 0,
    marketCap: tickerDetails.profile.mktCap || tickerDetails.quote.marketCap || 0,
    dayHigh: tickerDetails.quote.dayHigh || 0,
    dayLow: tickerDetails.quote.dayLow || 0,
    yearHigh: tickerDetails.quote.yearHigh || 0,
    yearLow: tickerDetails.quote.yearLow || 0,
    priceAvg50: tickerDetails.quote.priceAvg50 || 0,
    priceAvg200: tickerDetails.quote.priceAvg200 || 0,
  };

  useEffect(() => {
    if (ticker) {
      get({ endpoint: `v1/tickers/details/${ticker}` })
        .then(({ data, isError }) => {
          if (!isError && data && typeof data !== 'string') {
            if (
              ticker === data.profile?.symbol.replace('^', '') ||
              ticker === data.quote?.symbol.replace('^', '')
            ) {
              setTickerDetails({
                profile: data.profile || emptyProfile,
                quote: data.quote || emptyQuote,
              });
            }
          }
        })
        .catch((err) => {
          Sentry.withScope((_scope) => {
            Sentry.captureException(err);
          });
          setTickerDetails(defaultTickerDetails);
        });
    }
  }, [ticker]);

  if (isLoading || !details.price) {
    return null;
  }

  return (
    <div className="mb-3 space-y-1">
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
        <div className="text-gray-400">
          Price: <span className="font-medium text-white">${details.price.toFixed(2)}</span>
        </div>
        <div className="text-gray-400">
          Mkt Cap: <span className="font-medium text-white">${abbreviateNumber(details.marketCap, 1)}</span>
        </div>
        <div className="text-gray-400">
          Day Low: <span className="font-medium text-white">${details.dayLow.toFixed(2)}</span>
        </div>
        <div className="text-gray-400">
          Day High: <span className="font-medium text-white">${details.dayHigh.toFixed(2)}</span>
        </div>
        <div className="text-gray-400">
          Year Low: <span className="font-medium text-white">${details.yearLow.toFixed(2)}</span>
        </div>
        <div className="text-gray-400">
          Year High: <span className="font-medium text-white">${details.yearHigh.toFixed(2)}</span>
        </div>
        <div className="text-gray-400">
          50-Day MA: <span className="font-medium text-white">${details.priceAvg50.toFixed(2)}</span>
        </div>
        <div className="text-gray-400">
          200-Day MA: <span className="font-medium text-white">${details.priceAvg200.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

export default SidebarTickerDetails;
